import { $t } from '@/i18n';
import { message } from '@utils/wuji-message';
import { openBranchMergeModal } from '@/components/branch/merge-modal';
import { BRANCH_API_TIMEOUT } from '@/components/branch/merge-modal/constants';
import wujiFetch from '@/utils/wujiFetch';
import { memoizedFetchGET } from '@utils/common';
import { BASE_API_PATH } from '@config/constant';
import { useRouter } from '@/router/useRouter';
import { getAppQuery } from '@utils/path';

const designBaseUrl = `${BASE_API_PATH}/xy/design/branch`;
const getUserBranchUrl = projectId => `${BASE_API_PATH}/xy/user/branch?projectid=${projectId}`;

export const MASTER_BRANCH = 'master';
const DEFAULT_BRANCH = {
  branch: MASTER_BRANCH,
  name: MASTER_BRANCH,
  id: MASTER_BRANCH,
  isDefault: true,
};

// 分支相关接口超时时间
const branchTimeout = 60 * 1000;

export default {
  state: {
    currentBranch: null,
    uncommitChanges: {},
    commitList: [],
    branchList: [],
  },
  actions: {
    // 创建分支
    async createBranch({ rootState }, data) {
      const name = data.name.trim();
      if (!name) throw new Error($t('k_1b53325a'));
      const result = await wujiFetch(`${designBaseUrl}?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          name,
          checkoutBranchId: 'master',
        }),
        timeout: branchTimeout,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    // 获取当前应用的所有分支
    async fetchBranchList({ commit, rootState }, { projectId, includeArchived = 0 }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}?projectid=${projectId || rootState.project.projectInfo?.id}&includeArchived=${includeArchived}`);
        commit('updateBranchList', result);
        return result;
      } catch (err) {
        message.error($t('k_b8fdcc77'));
      }
    },
    async fetchBranchByName({}, { projectId, name }) {
      return await wujiFetch(`${designBaseUrl}/git?projectid=${projectId}&name=${name}`);
    },
    async fetchBranchInfo({ rootState }, { branchId }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}/${branchId}?projectid=${rootState.project.projectInfo?.id}`);
        return result;
      } catch (err) {
        message.error($t('k_28718a38'));
      }
    },

    async deleteBranch({ commit, state, rootState }, { branchId }) {
      await wujiFetch(`${designBaseUrl}/${branchId}?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'DELETE',
      });
      commit('updateBranchList', [...state.branchList.filter(item => item.branch !== branchId)]);
    },
    // 获取用户正在开发中的分支
    async fetchCurrentBranch({ commit }, { projectId } = {}) {
      try {
        // 优先取query的branch
        try {
          const branchQuery = getAppQuery('branch');
          if (branchQuery) {
            const branchInfo = await wujiFetch(`${designBaseUrl}/${branchQuery}?projectid=${projectId}`);
            if (branchInfo) {
              commit('updateCurrentBranch', branchInfo);
              return branchInfo;
            }
          }
        } catch (err) {
          message.warn($t('k_78c193e9'));
        }
        // query的branch无效再取用户上次开发的分支
        const result = await memoizedFetchGET(getUserBranchUrl(projectId));
        const branch = result ? result.branch : DEFAULT_BRANCH;
        commit('updateCurrentBranch', branch);
        return branch;
      } catch (err) {
        console.error(err);
        message.error($t('k_931968db'));
      }
    },
    // 切换分支
    async checkoutBranch({ rootState }, { branch, name }) {
      try {
        const projectId = rootState.project.projectInfo?.id;
        const result = await wujiFetch(`${BASE_API_PATH}/xy/user/branch/checkout/${branch}?projectid=${projectId}`, {
          method: 'POST',
        });
        if (result) {
          const { router, route } = useRouter();
          const currentQuery = route.value.query;
          const { href = '' } = router.value.resolve({
            path: route.value.path,
            query: {
              ...currentQuery,
              projectid: projectId,
              branch,
            },
          });
          if (name) message.success($t('k_f1727d44', { arg_0: name }));
          window.location.href = href;
        } else {
          message.error($t('k_580147f2'));
        }
      } catch (err) {
        console.error(err);
        message.error($t('k_580147f2'));
      }
    },
    // 获取分支未提交的变更
    async fetchBranchUncommitChanges({ commit, state, rootState }, { projectId, branchId } = {}) {
      try {
        const proId = projectId || rootState.project.projectInfo?.id;
        const branch = branchId || state.currentBranch?.branch;
        const result = await wujiFetch(`${designBaseUrl}/${branch}/uncommitchanges?projectid=${proId}`, {
          timeout: branchTimeout,
        });
        if (!branchId || state.currentBranch?.branch === branchId) {
          commit('updateUncommitChanges', result);
        }
        return result;
      } catch (err) {
        message.error($t('k_d1f925cf'));
      }
    },
    // 提交分支变更
    async commitBranchChanges({ rootState, dispatch }, { branchId, data }) {
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/commit?projectid=${rootState.project.projectInfo?.id}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      dispatch('fetchBranchUncommitChanges', { branchId });
      return result;
    },
    // 获取分支的提交记录
    async fetchBranchCommits({ rootState, commit }, { branchId, projectId, page = 1, size = 20 }) {
      try {
        const url = `${designBaseUrl}/${branchId}/commit?projectid=${projectId || rootState.project.projectInfo?.id}`;
        const getTotal = wujiFetch(`${url}&count=1`);
        const getList = wujiFetch(
          `${url}&page=${page}&size=${size}&sort=_ctime&order=desc`,
          { method: 'GET', timeout: branchTimeout },
        );
        const [result, { total }] = await Promise.all([getList, getTotal]);
        commit('updateCommitList', result);
        return {
          page,
          total,
          result,
        };
      } catch (err) {
        message.error($t('k_e76f0e39'));
      }
    },
    // 获取分支的最新的提交
    async fetchBranchHeadCommit({ rootState }, { branchId, projectId }) {
      try {
        const result = await wujiFetch(`${designBaseUrl}/${branchId}/commit/head?projectid=${projectId || rootState.project.projectInfo?.id}`);
        return result;
      } catch (err) {
        message.error($t('k_88e18551'));
      }
    },
    // 撤销分支上的某条变更
    async discardBranchChange({ rootState, dispatch }, { branchId, data }) {
      const projectId = rootState.project.projectInfo.id;
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/discard?projectid=${projectId}`, {
        method: 'POST',
        body: JSON.stringify({
          data,
        }),
      });
      dispatch('fetchBranchUncommitChanges', { branchId });
      return result;
    },

    // 获取可拉取的最新commitId
    async getPullCommitId({ rootState, state }, { projectId, branchId, targetBranchId } = {}) {
      try {
        const proId = projectId || rootState.project.projectInfo?.id;
        const branch = branchId || state.currentBranch?.branch;
        const result = await wujiFetch(`${designBaseUrl}/${branch}/pullcommit?projectid=${proId}&targetbranch=${targetBranchId}`, {
          timeout: BRANCH_API_TIMEOUT,
        });
        return result;
      } catch (err) {
        message.error($t('k_50654f3b', { arg_0: err.message }));
        throw err;
      }
    },
    // 只读：当前分支是否落后目标分支及落后提交数（无自动提交副作用）
    async fetchBehindStatus({ rootState, state }, { projectId, branchId, targetBranchId = MASTER_BRANCH } = {}) {
      try {
        const proId = projectId || rootState.project.projectInfo?.id;
        const branch = branchId || state.currentBranch?.branch;
        if (!proId || !branch || branch === targetBranchId) {
          return { behind: false, behindCount: 0 };
        }
        const result = await wujiFetch(`${designBaseUrl}/${branch}/behind?projectid=${proId}&targetbranch=${targetBranchId}`, {
          timeout: BRANCH_API_TIMEOUT,
        });
        return {
          behind: Boolean(result?.behind),
          behindCount: Number(result?.behindCount) || 0,
        };
      } catch (err) {
        // 提示用接口失败时静默，避免打扰正常编辑
        return { behind: false, behindCount: 0 };
      }
    },
    // 获取两个分支的基准commit
    async getBaseCommit({ rootState }, { branchId, targetBranchId } = {}) {
      const result = await wujiFetch(`${designBaseUrl}/${branchId}/basecommit?projectid=${rootState.project.projectInfo?.id}&targetbranch=${targetBranchId}`, {
        timeout: BRANCH_API_TIMEOUT,
      });
      return result;
    },
    // 获取差异
    async fetchCommitDiff({ rootState }, { commitId1, commitId2, baseCommitId }) {
      const proId = rootState.project.projectInfo?.id;
      const commits = [commitId1, commitId2, baseCommitId].filter(item => !!item).join(',');
      const result = await wujiFetch(`${designBaseUrl}/commit/diff?projectid=${proId}&commits=${commits}`, {
        timeout: BRANCH_API_TIMEOUT,
      });
      return result;
    },

    async mergeBranch({ rootState }, { branchId, mergeBranchId }) {
      try {
        const proId = rootState.project.projectInfo?.id;
        const res = await wujiFetch(`${designBaseUrl}/${branchId}/conflict/${mergeBranchId}?projectid=${proId}`, {
          method: 'GET',
          timeout: BRANCH_API_TIMEOUT,
        });
        if (!res.diff) {
          message.warn($t('k_1768046a', { arg_0: mergeBranchId === 'master' ? $t('k_1c675f24') : $t('k_2952c591') }));
          return;
        }

        await openBranchMergeModal({
          projectId: proId,
          branchId,
          mergeBranchId,
          changes: res.data,
        });
      } catch (error) {
        message.error($t('k_877674a6', { arg_0: error.message }));
        throw error;
      }
    },

    async fetchCommitChanges({ rootState }, { commitId, branchId }) {
      const projectId = rootState.project.projectInfo.id;
      try {
        return await wujiFetch(`${designBaseUrl}/${branchId}/commit/${commitId}?projectid=${projectId || rootState.project.projectInfo?.id}`);
      } catch (err) {
        message.error($t('k_e76f0e39'));
      }
    },

    async resetCommitPreview({ rootState }, { commitId, branchId, projectId }) {
      const proId = projectId || rootState.project.projectInfo?.id;
      return await wujiFetch(`${designBaseUrl}/${branchId}/commit/reset/${commitId}/preview?projectid=${proId}`, {
        method: 'GET',
        timeout: branchTimeout,
      });
    },

    async resetCommit({ rootState }, { commitId, branchId, projectId }) {
      try {
        return await wujiFetch(`${designBaseUrl}/${branchId}/commit/reset/${commitId}?projectid=${projectId || rootState.project.projectInfo?.id}`, {
          method: 'POST',
        });
      } catch (err) {
        message.error($t('k_c1fc01d2'));
        throw err;
      }
    },
  },
  mutations: {
    resetBranch(state) {
      state.currentBranch = null;
    },
    updateBranchList(state, data) {
      state.branchList = data;
    },
    updateCommitList(state, data) {
      state.commitList = data;
    },
    updateCurrentBranch(state, data) {
      state.currentBranch = data;
    },
    updateUncommitChanges(state, data) {
      state.uncommitChanges = data;
    },
  },
};
